import { css } from '@emotion/react'
import { useMedia } from 'react-use'
import { memo } from 'react'
import MenuIcon from '@mui/icons-material/Menu'
import { Link, useRouteMatch } from 'react-router-dom'

import { ReactComponent as FallbackLogo } from '../../shared/images/CollegeStateLogo.svg'
import { BP_MEDIUM_DOWN } from '../../../lib/styles/breakpoints'
import { IconButton, PrimaryButton } from '../../shared/buttons'
import TopNavMenu from '../TopNavMenu'
import { Auth0User } from '../../../types'
import useTheme from '../../../lib/hooks/useTheme'
import useShowRoleSwitcher from '../../../lib/hooks/useShowRoleSwitcher'
import LoadBar from '../../shared/loading/LoadBar'
import StudentSearchAutoComplete from '../StudentSearchAutoComplete'
import { userHasPermission, userIsAdmin } from '../../../lib/authenticationUtils'
import { AdminUserPermission } from '../../../generated/graphql'
import { borderColor } from '../../../lib/styles/colors'

export type TopNavProps = {
  /** Optional logo url override, only used for testing/stories currently. */
  logoUrlOverride?: string
  /** A function to expand nav menu over page content */
  onExpand: () => void
  /** A function to log the user out */
  onLogout: () => void
  /** Whether the user is currently authenticated, from useAuth0 hook */
  isAuthenticated: boolean
  /** User object from useAuth0 */
  user: Auth0User | undefined
}

/** IMPORTANT: This code is duplicated in scheduler.
 * Verify any changes here are also made in scheduler.
 */
const TopNav = ({ logoUrlOverride, onExpand, onLogout, isAuthenticated, user }: TopNavProps) => {
  const isSmall = useMedia(`(${BP_MEDIUM_DOWN})`)
  const showRoleSwitcher = useShowRoleSwitcher()
  const { logoUrl } = useTheme()
  const logoSource = logoUrlOverride || logoUrl
  const isSearch = useRouteMatch('/search')
  const showStudentSearch =
    isAuthenticated &&
    userIsAdmin(user) &&
    userHasPermission(user, [
      AdminUserPermission.AccessStudents,
      AdminUserPermission.ViewAllStudents
    ])
  let gridPx = '110px'
  if (!isAuthenticated) {
    gridPx = '170px'
  } else if (showRoleSwitcher) {
    // The fixed 110px track only fits the avatar + menu. When the persona
    // switcher chip is present, let the column size to its content so the chip
    // doesn't overflow and overlap the profile/menu.
    gridPx = 'auto'
  }
  const topNavCss = css`
    background: #ffffff;
    border-bottom: 1px solid ${borderColor};
    height: 56px;
    position: sticky;
    display: grid;
    grid-template-columns: 240px 1fr ${gridPx};
    grid-template-rows: 1fr 4px;
    top: 0;
    z-index: 11;

    @media (${BP_MEDIUM_DOWN}) {
      column-gap: 5px;
    }

    svg {
      display: block;
    }

    @media print {
      display: none;
    }
  `

  const topNavContainerCss = css`
    align-items: flex-start;
    display: flex;
    grid-column: 1;
    grid-row: 1;
    ${!isSmall && 'padding: 0 26px;'}
  `

  const logoCss = css`
    align-self: center;
    max-height: 52px;
    max-width: 100%;
    padding-top: 2px;

    @media (${BP_MEDIUM_DOWN}) {
      max-width: 80%;
    }
  `

  const loginButtonCss = css`
    align-self: center;
    grid-column: 3;
    text-decoration: none;
    button {
      width: 160px;
    }
  `
  const studentSearchContainerCss = css`
    grid-row: 1;
    grid-column: 2;
  `

  const topNavMenuContainerCss = css`
    grid-column: 3;
    grid-row: 1;
    padding-top: 4px;
  `

  const menuIconContainerCss = css`
    align-items: flex-end;
    display: flex;
    height: 52px;
  `

  let logoImg = <FallbackLogo aria-hidden="true" tabIndex={-1} />
  if (logoSource) {
    logoImg = <img css={logoCss} src={logoSource} alt="Logo" />
  }

  return (
    // <nav> label should omit the word "navigation"
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Navigation_Role
    <nav css={topNavCss} aria-label="Secondary">
      <div css={topNavContainerCss}>
        {isSmall && (
          <div css={menuIconContainerCss}>
            <IconButton ariaLabel="Open Navigation Menu" onClick={onExpand}>
              <MenuIcon />
            </IconButton>
          </div>
        )}
        {logoImg}
      </div>
      {isAuthenticated && showStudentSearch && (
        <div css={studentSearchContainerCss}>
          <StudentSearchAutoComplete />
        </div>
      )}
      {isAuthenticated && (
        <div css={topNavMenuContainerCss}>
          <TopNavMenu onLogout={onLogout} user={user} />
        </div>
      )}
      {!isAuthenticated && isSearch && (
        <Link to="/login" css={loginButtonCss}>
          <PrimaryButton>Log In</PrimaryButton>
        </Link>
      )}
      <LoadBar />
    </nav>
  )
}

export default memo(TopNav)
